import React, { useState, useEffect } from 'react';
import { Menu, X, LogOut, ChevronDown, UserPlus, Info, Landmark, Compass, Scale, PlusCircle, Sparkles, Users, Shield, Target, BookOpen, FileText } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoginDialog from './LoginDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from './ui/dropdown-menu';

const DiscordIcon = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    <path d="M20.3 4.37A19.8 19.8 0 0 0 15.5 3l-.24.5a18.3 18.3 0 0 1 4.3 1.4c-5-2.2-11-2.2-16.1 0A18.3 18.3 0 0 1 7.7 3.5L7.5 3a19.8 19.8 0 0 0-4.9 1.37C.4 8.77-.25 13.06.1 17.3a20 20 0 0 0 6 3c.5-.7.9-1.4 1.3-2.1a13 13 0 0 1-2-.96c.17-.12.34-.25.5-.38 3.9 1.84 8.1 1.84 12 0 .17.13.34.26.5.38a13 13 0 0 1-2 .96c.4.7.8 1.4 1.3 2.1a20 20 0 0 0 6-3c.4-4.94-.7-9.2-3.4-12.93ZM8.5 14.9c-1.2 0-2.2-1.1-2.2-2.4 0-1.3 1-2.4 2.2-2.4 1.2 0 2.2 1.1 2.2 2.4 0 1.3-1 2.4-2.2 2.4Zm7 0c-1.2 0-2.2-1.1-2.2-2.4 0-1.3 1-2.4 2.2-2.4 1.2 0 2.2 1.1 2.2 2.4 0 1.3-1 2.4-2.2 2.4Z"/>
  </svg>
);

const BRANCHES = [
  { slug: 'askeri-inzibat', label: 'Askeri İnzibat', short: 'AS.İZ' },
  { slug: 'ozel-kuvvetler', label: 'Özel Kuvvetler', short: 'ÖKK' },
  { slug: 'hava-kuvvetleri', label: 'Hava Kuvvetleri', short: 'HKK' },
  { slug: 'kara-kuvvetleri', label: 'Kara Kuvvetleri', short: 'KKK' },
  { slug: 'jandarma', label: 'Jandarma', short: 'JGK' },
  { slug: 'sinir-mufettisleri', label: 'Sınır Müfettişleri', short: 'SM' },
];

const KESIF = [
  { label: 'Wiki', to: '/wiki', icon: BookOpen },
  { label: 'Ekip', to: '/#team', icon: Users },
  { label: 'Rütbeler', to: '/#stats', icon: Shield },
];
const YASAL = [
  { label: 'Gizlilik Politikası', to: '#', icon: FileText },
  { label: 'Kullanım Şartları', to: '#', icon: FileText },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (href) => {
    if (href.startsWith('/#')) {
      if (location.pathname === '/') {
        const el = document.querySelector(href.slice(1));
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else navigate(href);
    } else navigate(href);
  };

  return (
    <>
      <header className={`sticky top-0 z-50 nav-root ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="nav-inner max-w-7xl mx-auto px-5 lg:px-8 h-[64px] flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-0.5 font-black text-2xl tracking-tight">
            <span className="text-rose-500">T</span><span>O</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <button onClick={() => go('/#about')} className="nav-item flex items-center gap-1.5"><Info size={14} /> Hakkımızda</button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="nav-item flex items-center gap-1.5"><Landmark size={14} /> Ordu <ChevronDown size={12} /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64">
                <DropdownMenuLabel>Branşlar</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {BRANCHES.map(b => (
                  <DropdownMenuItem key={b.slug} onClick={() => navigate(`/branch/${b.slug}`)} className="cursor-pointer">
                    <span className="w-10 text-[10px] font-bold text-rose-500">{b.short}</span>
                    <span>{b.label}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => go('/#team')} className="cursor-pointer">
                  <Users size={14} className="mr-2" /> Ordu Yönetimi
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="nav-item flex items-center gap-1.5"><Compass size={14} /> Keşfet <ChevronDown size={12} /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                {KESIF.map(l => (
                  <DropdownMenuItem key={l.label} onClick={() => go(l.to)} className="cursor-pointer">
                    <l.icon size={14} className="mr-2" /> {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="nav-item flex items-center gap-1.5"><Scale size={14} /> Yasal <ChevronDown size={12} /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                {YASAL.map(l => (
                  <DropdownMenuItem key={l.label} onClick={() => go(l.to)} className="cursor-pointer">
                    <l.icon size={14} className="mr-2" /> {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          <div className="flex items-center gap-2">
            <a href="https://www.roblox.com/communities/14905199/I-TO-I-Turkish-Armed-Forces#!/about" target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex pill-btn text-[13px] font-bold uppercase tracking-wider" style={{ background: '#e11d48', color: '#fff', borderColor: 'transparent' }}>
              <UserPlus size={14} />
              <span>Katıl</span>
            </a>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="user-pill">
                    {user.avatar ? <img src={user.avatar} alt="" className="w-7 h-7 rounded-full" /> : <UserPlus size={18} />}
                    <span className="hidden sm:inline font-medium">{user.discord_displayName || user.discord_username}</span>
                    {user.isAdmin && <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-800 font-semibold">ADMIN</span>}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel className="flex items-center gap-2">
                    {user.avatar && <img src={user.avatar} className="w-8 h-8 rounded-full" alt="" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{user.discord_displayName}</div>
                      <div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                        @{user.discord_username}{user.isAdmin && ' · Admin'}{!user.isAdmin && user.canEdit && ' · Yetkili'}
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {user.canEdit && (
                    <DropdownMenuItem onClick={() => navigate('/wiki/new')} className="cursor-pointer"><PlusCircle size={14} className="mr-2" /> Yeni Wiki Yazısı</DropdownMenuItem>
                  )}
                  {user.isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer"><Sparkles size={14} className="mr-2 text-amber-500" /> Admin Panel</DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="cursor-pointer text-rose-500"><LogOut size={14} className="mr-2" /> Çıkış</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button onClick={() => setLoginOpen(true)} className="pill-btn text-[13px] font-bold uppercase tracking-wider" style={{ background: '#5865F2', color: '#fff', borderColor: 'transparent' }}>
                <DiscordIcon />
                <span>Giriş Yap</span>
              </button>
            )}
            <button className="md:hidden p-2 rounded-lg transition-colors hover:bg-black/5 dark:hover:bg-white/5" onClick={() => setOpen(v => !v)} aria-label="Menu">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        <div className={`md:hidden overflow-hidden transition-all duration-300 ${open ? 'max-h-[480px]' : 'max-h-0'}`} style={{ background: 'var(--bg)', borderTop: open ? '1px solid var(--border)' : 'none' }}>
          <div className="px-5 py-4 flex flex-col gap-2">
            <button onClick={() => { go('/#about'); setOpen(false); }} className="nav-item-mobile text-left">Hakkımızda</button>
            <div className="nav-item-mobile font-semibold opacity-60">Ordu</div>
            {BRANCHES.map(b => (
              <button key={b.slug} onClick={() => { navigate(`/branch/${b.slug}`); setOpen(false); }} className="nav-item-mobile text-left pl-6">{b.short} — {b.label}</button>
            ))}
            <button onClick={() => { go('/#team'); setOpen(false); }} className="nav-item-mobile text-left pl-6">Ordu Yönetimi</button>
            <Link to="/wiki" onClick={() => setOpen(false)} className="nav-item-mobile">Wiki</Link>
          </div>
        </div>
      </header>
      <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
    </>
  );
}
